import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  arrayRemove,
  type QueryConstraint,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import type { TaskBoard, TaskBoardList } from '@/types'
import type { TaskBoardsState } from './state'

// Firestore rejects undefined values — strip them from list objects
const sanitizeList = (l: TaskBoardList) => ({
  id: l.id,
  name: l.name,
  position: l.position,
  ...(l.color ? { color: l.color } : {}),
})

const DEFAULT_LISTS: Omit<TaskBoardList, 'id'>[] = [
  { name: 'Backlog', position: 0 },
  { name: 'Por hacer', position: 1 },
  { name: 'En progreso', position: 2 },
  { name: 'Revision', position: 3 },
  { name: 'Hecho', position: 4 },
  { name: 'Bloqueado', position: 5 },
]

const mapBoardData = (docSnap: { id: string; data: () => Record<string, unknown> }): TaskBoard => {
  const data = docSnap.data()
  const lists = (data.lists as Array<Record<string, unknown>>) || []
  return {
    id: docSnap.id,
    tenantId: (data.tenantId as string) || undefined,
    name: (data.name as string) || '',
    description: (data.description as string) || undefined,
    color: (data.color as string) || undefined,
    lists: lists.map((l) => ({
      id: (l.id as string) || '',
      name: (l.name as string) || '',
      color: (l.color as string) || undefined,
      position: (l.position as number) ?? 0,
    })),
    members: (data.members as string[]) || [],
    archived: (data.archived as boolean) || false,
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    updatedAt: (data.updatedAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    createdBy: (data.createdBy as string) || '',
    updatedBy: (data.updatedBy as string) || '',
  }
}

export const createActions = (state: TaskBoardsState) => {
  const fetchBoards = async (): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    const role = authStore.user?.role
    if (!userId) return

    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getTenantConstraint()
      const constraints: QueryConstraint[] = [orderBy('createdAt', 'asc')]
      if (tenantConstraint) constraints.unshift(tenantConstraint)

      const q = query(collection(db, 'taskBoards'), ...constraints)
      const snapshot = await getDocs(q)
      let boards = snapshot.docs.map(mapBoardData)

      // Superadmin sees all, everyone else only their own
      if (role !== 'superadmin') {
        boards = boards.filter((b) =>
          b.createdBy === userId || b.members.includes(userId)
        )
      }

      state.boards = boards
    } catch (error) {
      console.error('Error fetching boards:', error)
      state.error = 'Error al cargar tableros'
    } finally {
      state.isLoading = false
    }
  }

  const createBoard = async (data: { name: string; description?: string; color?: string; lists?: Omit<TaskBoardList, 'id'>[]; members?: string[] }): Promise<TaskBoard | null> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return null

    try {
      const lists: TaskBoardList[] = (data.lists || []).map((l, i) => ({
        id: crypto.randomUUID(),
        name: l.name,
        color: l.color,
        position: l.position ?? i,
      }))

      const members = data.members && data.members.length > 0
        ? (data.members.includes(userId) ? data.members : [userId, ...data.members])
        : [userId]

      const docData = {
        ...getTenantData(),
        name: data.name,
        description: data.description || null,
        color: data.color || null,
        lists: lists.map(sanitizeList),
        members,
        archived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      }

      const docRef = await addDoc(collection(db, 'taskBoards'), docData)

      const board: TaskBoard = {
        id: docRef.id,
        tenantId: getTenantData().tenantId,
        name: data.name,
        description: data.description,
        color: data.color,
        lists,
        members,
        archived: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: userId,
        updatedBy: userId,
      }

      state.boards.push(board)
      return board
    } catch (error) {
      console.error('Error creating board:', error)
      state.error = 'Error al crear tablero'
      throw error
    }
  }

  const updateBoard = async (boardId: string, data: { name?: string; description?: string; color?: string; members?: string[] }): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return

    try {
      // Firestore RECHAZA campos `undefined` (ignoreUndefinedProperties no está activo) → si
      // esparcíamos `data` crudo, un description/color vacío tumbaba TODA la escritura y ni el
      // nombre ni los miembros persistían (el error se tragaba y la UI mostraba "actualizado").
      // Saneamos igual que createBoard: solo incluimos claves provistas; description/color vacíos → null.
      const payload: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      }
      if (data.name !== undefined) payload.name = data.name
      if (data.members !== undefined) payload.members = data.members
      if ('description' in data) payload.description = data.description ?? null
      if ('color' in data) payload.color = data.color ?? null

      await updateDoc(doc(db, 'taskBoards', boardId), payload)

      const idx = state.boards.findIndex((b) => b.id === boardId)
      if (idx !== -1) {
        state.boards[idx] = { ...state.boards[idx], ...data, updatedAt: new Date() }
      }
    } catch (error) {
      console.error('Error updating board:', error)
      state.error = 'Error al actualizar tablero'
      throw error
    }
  }

  const deleteBoard = async (boardId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'taskBoards', boardId))
      state.boards = state.boards.filter((b) => b.id !== boardId)
    } catch (error) {
      console.error('Error deleting board:', error)
      state.error = 'Error al eliminar tablero'
      throw error
    }
  }

  const addList = async (boardId: string, name: string, color?: string): Promise<TaskBoardList | null> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return null

    const board = state.boards.find((b) => b.id === boardId)
    if (!board) return null

    const newList: TaskBoardList = {
      id: crypto.randomUUID(),
      name,
      color,
      position: board.lists.length,
    }

    const updatedLists = [...board.lists, newList]

    try {
      await updateDoc(doc(db, 'taskBoards', boardId), {
        lists: updatedLists.map(sanitizeList),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      const idx = state.boards.findIndex((b) => b.id === boardId)
      if (idx !== -1) {
        state.boards[idx] = { ...state.boards[idx], lists: updatedLists, updatedAt: new Date() }
      }

      return newList
    } catch (error) {
      console.error('Error adding list:', error)
      state.error = 'Error al agregar lista'
      throw error
    }
  }

  const updateList = async (boardId: string, listId: string, data: { name?: string; color?: string }): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return

    const board = state.boards.find((b) => b.id === boardId)
    if (!board) return

    const updatedLists = board.lists.map((l) =>
      l.id === listId ? { ...l, ...data } : l
    )

    try {
      await updateDoc(doc(db, 'taskBoards', boardId), {
        lists: updatedLists.map(sanitizeList),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      const idx = state.boards.findIndex((b) => b.id === boardId)
      if (idx !== -1) {
        state.boards[idx] = { ...state.boards[idx], lists: updatedLists, updatedAt: new Date() }
      }
    } catch (error) {
      console.error('Error updating list:', error)
      state.error = 'Error al actualizar lista'
    }
  }

  const removeList = async (boardId: string, listId: string): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return

    const board = state.boards.find((b) => b.id === boardId)
    if (!board) return

    const updatedLists = board.lists
      .filter((l) => l.id !== listId)
      .map((l, i) => ({ ...l, position: i }))

    try {
      await updateDoc(doc(db, 'taskBoards', boardId), {
        lists: updatedLists.map(sanitizeList),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      const idx = state.boards.findIndex((b) => b.id === boardId)
      if (idx !== -1) {
        state.boards[idx] = { ...state.boards[idx], lists: updatedLists, updatedAt: new Date() }
      }
    } catch (error) {
      console.error('Error removing list:', error)
      state.error = 'Error al eliminar lista'
    }
  }

  const reorderLists = async (boardId: string, orderedListIds: string[]): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return

    const board = state.boards.find((b) => b.id === boardId)
    if (!board) return

    const updatedLists = orderedListIds
      .map((id, i) => {
        const list = board.lists.find((l) => l.id === id)
        return list ? { ...list, position: i } : null
      })
      .filter((l): l is TaskBoardList => l !== null)

    try {
      await updateDoc(doc(db, 'taskBoards', boardId), {
        lists: updatedLists.map(sanitizeList),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      const idx = state.boards.findIndex((b) => b.id === boardId)
      if (idx !== -1) {
        state.boards[idx] = { ...state.boards[idx], lists: updatedLists, updatedAt: new Date() }
      }
    } catch (error) {
      console.error('Error reordering lists:', error)
      state.error = 'Error al reordenar listas'
    }
  }

  const addMember = async (boardId: string, userId: string): Promise<void> => {
    const authStore = useAuthStore()
    const currentUserId = authStore.user?.id
    if (!currentUserId) return

    const board = state.boards.find((b) => b.id === boardId)
    if (!board || board.members.includes(userId)) return

    const updatedMembers = [...board.members, userId]

    try {
      await updateDoc(doc(db, 'taskBoards', boardId), {
        members: updatedMembers,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      const idx = state.boards.findIndex((b) => b.id === boardId)
      if (idx !== -1) {
        state.boards[idx] = { ...state.boards[idx], members: updatedMembers, updatedAt: new Date() }
      }
    } catch (error) {
      console.error('Error adding member:', error)
      throw error
    }
  }

  // Quita a un usuario del tablero. Con opts.unassignFromTasks además lo DESASIGNA de todas
  // las tarjetas del board → pierde acceso total (sin la flag, en su vista global sigue viendo
  // las tarjetas que tenga asignadas). Visibilidad client-side; el límite duro es el tenant.
  const removeMember = async (
    boardId: string,
    userId: string,
    opts?: { unassignFromTasks?: boolean },
  ): Promise<void> => {
    const authStore = useAuthStore()
    const currentUserId = authStore.user?.id
    if (!currentUserId) return

    const board = state.boards.find((b) => b.id === boardId)
    if (!board) return

    const updatedMembers = board.members.filter((m) => m !== userId)

    try {
      await updateDoc(doc(db, 'taskBoards', boardId), {
        members: updatedMembers,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      })

      if (opts?.unassignFromTasks) {
        // Traemos las tareas del board y filtramos en memoria las que tengan al user asignado
        // (evita exigir el índice compuesto boardId== + assignedTo array-contains).
        const snap = await getDocs(
          query(collection(db, 'tasks'), where('boardId', '==', boardId)),
        )
        const affected = snap.docs.filter((d) =>
          ((d.data().assignedTo as string[] | undefined) || []).includes(userId),
        )
        // writeBatch en chunks de 450 (límite Firestore 500). Si el user era responsable
        // (assignedTo[0]), la tarjeta queda sin responsable — comportamiento aceptado.
        for (let i = 0; i < affected.length; i += 450) {
          const batch = writeBatch(db)
          for (const d of affected.slice(i, i + 450)) {
            batch.update(d.ref, {
              assignedTo: arrayRemove(userId),
              updatedAt: serverTimestamp(),
              updatedBy: currentUserId,
            })
          }
          await batch.commit()
        }
      }

      const idx = state.boards.findIndex((b) => b.id === boardId)
      if (idx !== -1) {
        state.boards[idx] = { ...state.boards[idx], members: updatedMembers, updatedAt: new Date() }
      }
    } catch (error) {
      console.error('Error removing member:', error)
      throw error
    }
  }

  const createDefaultBoard = async (): Promise<TaskBoard | null> => {
    if (state.boards.length > 0) return state.boards[0]
    return createBoard({
      name: 'General',
      description: 'Tablero principal',
      lists: DEFAULT_LISTS,
    })
  }

  return {
    fetchBoards,
    createBoard,
    updateBoard,
    deleteBoard,
    addList,
    updateList,
    removeList,
    reorderLists,
    addMember,
    removeMember,
    createDefaultBoard,
  }
}
